import { useEffect, useState } from "react";
import type { IAnswer, IQuestion } from "@/types/types";
import { createAnswersList } from "../inspection.util";
import EmptyAnwer from "./answer/EmptyAnswer";
import OpenAnswer from "./answer/OpenAnswer";
import { Icon } from "@/components/icons/Icon";
import Button from "@/components/buttons/button/Button";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import CloseInspectionForm from "./CloseInspectionForm";

interface AnswerColumnProps {
  answers: IAnswer[];
  questions: IQuestion[];
  closedColumn?: boolean;
  columnName: string;
  closeWhitAllNa: (esnOut?: string) => void;
  askClosePhaseWithNa: () => void;
}

const AnswerColumn = ({
  columnName,
  answers = [],
  questions = [],
  closedColumn,
  closeWhitAllNa,
  askClosePhaseWithNa,
}: AnswerColumnProps) => {
  const [answersList, setAnswersList] = useState<any[]>([]);
  const [closeInspectionModal, setCloseInspectionModal] = useState(false);

  const translate = useTranslate();

  useEffect(() => {
    if (closedColumn) {
      if (questions.length && answers.length) {
        const newAnswersList = createAnswersList(questions, answers);
        setAnswersList(newAnswersList);
      }
    } else {
      const newAnswersList = createAnswersList(questions, answers);
      setAnswersList(newAnswersList);
    }
  }, [questions, answers, closedColumn]);

  const disabledNAButton =
    answers.length > 0 && answers.some((answer) => answer.status !== "N/A");

  const closePhaseWithNa = () => {
    if (disabledNAButton) return;
    askClosePhaseWithNa();
  };

  const closeInspectionWithNaLastPhase = async (
    phaseId: number,
    esnOut: string,
  ) => {
    if (!esnOut.length || phaseId !== 4 || disabledNAButton) return;
    closeWhitAllNa(esnOut);
  };

  return (
    <div className="bg-ib-gray-lightest-2 px-3 rounded-sm py-4 flex flex-col gap-y-4 min-w-80">
      <div className="flex w-full justify-between h-8">
        <div className="text-ib-main font-ib-bold flex flex-row gap-x-1 items-center">
          <Icon name="lockOpen" className="size-4 text-ib-black" />
          <span className="text-ib-gray-darksest over overflow-x-hidden whitespace-nowrap text-ellipsis">
            {columnName}
          </span>
        </div>
        {columnName === translate("disassemblyInspection") ||
        columnName === translate("mdpRepair") ? (
          <div>
            <Button
              onClick={closePhaseWithNa}
              label={translate("naPhase")}
              type="secondary-red"
              size="small"
              disabled={disabledNAButton}
            />
          </div>
        ) : null}
        {columnName === translate("ccInspection") ? (
          <div>
            <Button
              onClick={() => setCloseInspectionModal(true)}
              label={translate("naPhase")}
              type="secondary-red"
              size="small"
              disabled={disabledNAButton}
            />
          </div>
        ) : null}
      </div>
      <>
        {answersList.map((answer, idx) => {
          if (answer.empty) {
            return (
              <EmptyAnwer
                key={`empty-${columnName}-${answer.questionId}`}
                questionId={answer.questionId}
              />
            );
          } else {
            return (
              <OpenAnswer
                key={`${columnName}-${answer.questionId}`}
                item={answer}
                idx={idx + 1}
              />
            );
          }
        })}
      </>
      {closeInspectionModal ? (
        <CloseInspectionForm
          closeInspection={closeInspectionWithNaLastPhase}
          isNaClose
          cancel={() => setCloseInspectionModal(false)}
        />
      ) : null}
    </div>
  );
};

export default AnswerColumn;
